// src/features/fourlink-config/components/blocks/Block1Steps/PlanesStep.jsx

import React, { useMemo, useState } from 'react';
import { CommentButton } from '../../shared/CommentButton';

export const PlanesStep = ({
  planesDisponibles,
  hiddenPlanes,
  selectedPlanes,
  onTogglePlan,
  onAddPlan,
  onHidePlanes,
  onRestorePlan,
  onCommentClick,
  hasComment,
}) => {
  const planesSectionKey = 'section:planes';
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isHideModalOpen, setIsHideModalOpen] = useState(false);
  const [newPlanName, setNewPlanName] = useState('');
  const [planesToHide, setPlanesToHide] = useState([]);

  const planesVisibles = useMemo(
    () => (Array.isArray(planesDisponibles) ? planesDisponibles : []),
    [planesDisponibles],
  );

  const hiddenList = useMemo(
    () => (Array.isArray(hiddenPlanes) ? hiddenPlanes : []),
    [hiddenPlanes],
  );

  const togglePlanToHide = (plan) => {
    setPlanesToHide((prev) => (prev.includes(plan) ? prev.filter((p) => p !== plan) : [...prev, plan]));
  };

  const handleConfirmHide = () => {
    if (typeof onHidePlanes === 'function' && planesToHide.length > 0) {
      onHidePlanes(planesToHide);
    }
    setPlanesToHide([]);
    setIsHideModalOpen(false);
  };

  const handleCreatePlan = (e) => {
    e.preventDefault();
    if (!newPlanName.trim()) return;
    const created = typeof onAddPlan === 'function' ? onAddPlan(newPlanName.trim()) : false;
    if (created) {
      setNewPlanName('');
      setIsAddModalOpen(false);
    }
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setNewPlanName('');
  };

  const handleCloseHideModal = () => {
    setIsHideModalOpen(false);
    setPlanesToHide([]);
  };

  const renderModalShell = (children) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 relative">
        {children}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-2xl font-bold text-gray-800">Planes</h2>
        <div className="flex items-center gap-3">
          {hasComment && (
            <span className="text-xs font-semibold text-blue-700">Comentario guardado</span>
          )}
          <button
            type="button"
            onClick={() => setIsHideModalOpen(true)}
            className="relative inline-flex items-center justify-center w-10 h-10 rounded-full border border-red-200 text-red-700 bg-white hover:bg-red-50 transition-colors font-semibold"
            title="Ocultar plan"
          >
            <span className="sr-only">Ocultar plan</span>
            <span className="text-lg leading-none font-bold">-</span>
          </button>
          <button
            type="button"
            onClick={() => setIsAddModalOpen(true)}
            className="relative inline-flex items-center justify-center w-10 h-10 rounded-full border border-green-200 text-green-700 bg-white hover:bg-green-50 transition-colors"
            title="Añadir plan"
          >
            <span className="text-lg leading-none font-bold">+</span>
            <span className="sr-only">Anadir plan</span>
          </button>
          <CommentButton
            onClick={() => onCommentClick({ key: planesSectionKey, label: 'Planes' })}
            hasComment={hasComment}
            srLabel="Añadir comentario para Planes"
            variant="circle"
            className="border-blue-200 text-blue-700 bg-white hover:bg-blue-50"
          />
        </div>
      </div>
      <p className="text-gray-600 mb-6">Selecciona los planes predefinidos:</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {planesVisibles.length > 0 ? (
          planesVisibles.map(plan => (
            <button
              key={plan}
              onClick={() => onTogglePlan(plan)}
              className={`p-4 rounded-lg border-2 transition-all text-left ${
                selectedPlanes.includes(plan)
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 hover:border-blue-300'
              }`}
            >
              {plan}
            </button>
          ))
        ) : (
          <div className="col-span-full text-gray-500 italic">
            No hay planes disponibles.
          </div>
        )}
      </div>

      {isAddModalOpen && renderModalShell(
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Añadir plan</h3>
              <p className="text-sm text-gray-600">Crea nuevos planes y revisa los que has ocultado.</p>
            </div>
            <button
              type="button"
              className="text-gray-500 hover:text-gray-800"
              onClick={handleCloseAddModal}
              aria-label="Cerrar modal de añadir plan"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleCreatePlan} className="space-y-3">
            <label className="block text-sm font-medium text-gray-700" htmlFor="nuevo-plan-input">
              Nombre del plan
            </label>
            <input
              id="nuevo-plan-input"
              type="text"
              value={newPlanName}
              onChange={(e) => setNewPlanName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Ej. Plan Premium"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleCloseAddModal}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                disabled={!newPlanName.trim()}
              >
                Añadir plan
              </button>
            </div>
          </form>

          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-800 mb-2">Planes ocultos</h4>
            {hiddenList.length === 0 ? (
              <p className="text-sm text-gray-500">No hay planes ocultos.</p>
            ) : (
              <ul className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {hiddenList.map((item) => (
                  <li key={item} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                    <span className="text-sm text-gray-800">{item}</span>
                    {typeof onRestorePlan === 'function' && (
                      <button
                        type="button"
                        onClick={() => onRestorePlan(item)}
                        className="text-xs px-3 py-1 rounded-full border border-blue-200 text-blue-700 hover:bg-blue-50"
                      >
                        Restaurar
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {isHideModalOpen && renderModalShell(
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Ocultar planes</h3>
              <p className="text-sm text-gray-600">Selecciona los planes que quieras ocultar temporalmente.</p>
            </div>
            <button
              type="button"
              className="text-gray-500 hover:text-gray-800"
              onClick={handleCloseHideModal}
              aria-label="Cerrar modal de ocultar planes"
            >
              ✕
            </button>
          </div>

          {planesVisibles.length === 0 ? (
            <p className="text-sm text-gray-500">No hay planes disponibles para ocultar.</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {planesVisibles.map((plan) => (
                <label
                  key={plan}
                  className="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2 hover:border-blue-200"
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={planesToHide.includes(plan)}
                      onChange={() => togglePlanToHide(plan)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-800">{plan}</span>
                  </div>
                  {selectedPlanes.includes(plan) && (
                    <span className="text-xs text-blue-600 font-medium">Seleccionado</span>
                  )}
                </label>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={handleCloseHideModal}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirmHide}
              className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
              disabled={planesToHide.length === 0}
            >
              Ocultar seleccionados
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
